import {
  CategoryGroup,
  CompanyRole,
  MemberCategoryTotal,
  MemberDays,
  MemberRole,
  RoleGrant,
  SharedCategory,
  TeamMember,
} from '../../../model/team.model';

// Mock data for UI demonstration — no endpoints exist yet.
// The window is two full weeks: the previous week plus the current one, Monday first.
// Weekends therefore sit at indices 5, 6, 12 and 13.

export const members: TeamMember[] = [
  { id: 'anna', name: 'Anna Bennett', email: 'anna@northwind.co', team: 'Design', status: 'active' },
  { id: 'marcus', name: 'Marcus Reid', email: 'marcus@northwind.co', team: 'Engineering', status: 'active' },
  { id: 'oliver', name: 'Oliver Grant', email: 'oliver@northwind.co', team: 'Engineering', status: 'active' },
  { id: 'maria', name: 'Maria Lopez', email: 'maria@northwind.co', team: 'Management', status: 'active' },
  { id: 'peter', name: 'Peter Shaw', email: 'peter@northwind.co', team: 'Engineering', status: 'active' },
  { id: 'dylan', name: 'Dylan Ross', email: 'dylan@northwind.co', team: 'QA', status: 'active' },
  { id: 'nina', name: 'Nina Cole', email: 'nina@northwind.co', team: 'Design', status: 'invited' },
];

/** Only people who actually joined show up in coverage and reports. */
export const activeMembers = members.filter(member => member.status === 'active');

export const categoryGroups: CategoryGroup[] = [
  { id: 'product', name: 'Product' },
  { id: 'communication', name: 'Communication' },
  { id: 'other', name: 'Other' },
];

export const categories: SharedCategory[] = [
  { id: 'feature', name: 'Feature work', groupId: 'product', color: '#4d7ab4', archived: false },
  { id: 'bugfix', name: 'Bugfixing', groupId: 'product', color: '#b4674d', archived: false },
  { id: 'review', name: 'Code review', groupId: 'product', color: '#7a5cb4', archived: false },
  { id: 'meetings', name: 'Meetings', groupId: 'communication', color: '#b4a14d', archived: false },
  { id: 'support', name: 'Support', groupId: 'communication', color: '#4db4a1', archived: false },
  { id: 'learning', name: 'Learning', groupId: 'other', color: '#6b7a5c', archived: false },
  { id: 'admin', name: 'Admin', groupId: 'other', color: '#8a8580', archived: false },
  { id: 'onsite', name: 'Client onsite', groupId: 'other', color: '#a0787a', archived: true },
];

export const roles: CompanyRole[] = [
  { id: 'everyone', name: 'Everyone', isDefault: true },
  { id: 'engineer', name: 'Engineer', isDefault: false },
  { id: 'designer', name: 'Designer', isDefault: false },
  { id: 'qa', name: 'QA', isDefault: false },
  { id: 'support-rota', name: 'Support rota', isDefault: false },
];

/**
 * "Everyone" hands out a whole group plus one leaf; "Engineer" hands out the whole
 * Product subtree. Both mechanisms are in play so the matrix shows inherited cells.
 */
export const roleGrants: RoleGrant[] = [
  { roleId: 'everyone', nodeId: 'other' },
  { roleId: 'everyone', nodeId: 'meetings' },

  { roleId: 'engineer', nodeId: 'product' },

  { roleId: 'designer', nodeId: 'feature' },

  { roleId: 'qa', nodeId: 'bugfix' },
  { roleId: 'qa', nodeId: 'review' },

  { roleId: 'support-rota', nodeId: 'support' },
];

export const memberRoles: MemberRole[] = [
  ...members.map(member => ({ memberId: member.id, roleId: 'everyone' })),
  { memberId: 'marcus', roleId: 'engineer' },
  { memberId: 'oliver', roleId: 'engineer' },
  { memberId: 'peter', roleId: 'engineer' },
  { memberId: 'anna', roleId: 'designer' },
  { memberId: 'dylan', roleId: 'qa' },
  { memberId: 'oliver', roleId: 'support-rota' },
  { memberId: 'dylan', roleId: 'support-rota' },
];

/** Peter never logs anything — the case the coverage view exists to surface. */
export const memberDays: MemberDays[] = [
  { memberId: 'anna', hours: [7.5, 8, 6.5, 8, 7, 0, 0, 8, 7.5, 8, 6, 7.5, 0, 0] },
  { memberId: 'marcus', hours: [8, 8, 8, 7, 6, 0, 0, 8, 8, 7.5, 8, 7, 0, 0] },
  { memberId: 'oliver', hours: [6, 0, 7, 8, 5, 0, 0, 7, 6.5, 0, 8, 6, 0, 0] },
  { memberId: 'maria', hours: [4, 5, 4.5, 5, 4, 0, 0, 5, 4, 4.5, 5, 3, 0, 0] },
  { memberId: 'peter', hours: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
  { memberId: 'dylan', hours: [8, 7, 8, 8, 8, 0, 0, 8, 8, 8, 7, 8, 0, 0] },
];

const split = (memberId: string, values: Record<string, number>): MemberCategoryTotal[] =>
  Object.entries(values).map(([categoryId, hours]) => ({ memberId, categoryId, hours }));

/** Each person's split adds up to their total in `memberDays` over the full window. */
export const memberCategoryTotals: MemberCategoryTotal[] = [
  ...split('anna', { feature: 30, bugfix: 8, review: 6, meetings: 18, support: 2, learning: 6, admin: 4 }),
  ...split('marcus', { feature: 40, bugfix: 14, review: 8, meetings: 8, support: 2, learning: 2, admin: 1.5 }),
  ...split('oliver', { feature: 22, bugfix: 12, review: 5, meetings: 7, support: 4, learning: 2, admin: 1.5 }),
  ...split('maria', { feature: 2, review: 4, meetings: 26, support: 4, learning: 3, admin: 5 }),
  ...split('dylan', { feature: 18, bugfix: 34, review: 6, meetings: 8, support: 10, admin: 2 }),
];

export const memberById = (id: string) => members.find(member => member.id === id);
export const categoryById = (id: string) => categories.find(category => category.id === id);
export const groupById = (id: string) => categoryGroups.find(group => group.id === id);

export const rolesOf = (memberId: string) =>
  memberRoles
    .filter(link => link.memberId === memberId)
    .map(link => roles.find(role => role.id === link.roleId)!)
    .filter(Boolean);

export const membersWithRole = (roleId: string) =>
  memberRoles.filter(link => link.roleId === roleId).length;

/**
 * Leaves a set of roles hands out. A granted group brings its whole subtree; archived
 * categories never leave the server.
 */
export const grantedLeaves = (
  roleIds: string[],
  grants: RoleGrant[] = roleGrants,
  cats: SharedCategory[] = categories,
  groups: CategoryGroup[] = categoryGroups,
) => {
  const result = new Set<string>();
  grants
    .filter(grant => roleIds.includes(grant.roleId))
    .forEach(grant => {
      const group = groups.find(item => item.id === grant.nodeId);
      if (group) {
        cats
          .filter(category => category.groupId === group.id && !category.archived)
          .forEach(category => result.add(category.id));
      } else {
        const category = cats.find(item => item.id === grant.nodeId);
        if (category && !category.archived) result.add(category.id);
      }
    });
  return result;
};

export const formatHours = (value: number) =>
  (Math.round(value * 10) / 10).toFixed(1).replace(/\.0$/, '');

/** Weekends sit at these positions in the 14-day window. */
export const isWeekendIndex = (index: number) => index % 7 >= 5;
